import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Description as DocumentIcon,
  Visibility as PreviewIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/layout/Esign";
import config from "../config.js";

const EsignHistory = () => {
  const { token } = useSelector((state) => state.auth);
  const { API_BASE_URL } = config;
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const getEsignDocuments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/v1/getUserEDocument`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const documentsList = Array.isArray(response.data) 
        ? response.data 
        : response.data.data || [];
        
      setDocuments(documentsList);
    } catch (error) {
      console.error('Error details:', error);
      setError(error.response?.data?.message || "Failed to fetch documents");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = (documentUrl) => {
    setPreviewUrl(documentUrl);
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/deleteUserEDocument/${docId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Remove document from state
      setDocuments(prev => prev.filter(doc => doc._id !== docId));
      setDeleteDialogOpen(false);
      setSelectedDoc(null);
    } catch (error) {
      console.error('Delete error:', error);
      setError("Failed to delete document");
    }
  };

  useEffect(() => {
    getEsignDocuments();
  }, []);

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const documentsList = Array.isArray(documents) ? documents : [];

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" gutterBottom component="div" sx={{ mb: 3 }}>
              eSign History
            </Typography>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              </motion.div>
            )}

            {documentsList.length === 0 && !error ? (
              <Alert severity="info">No documents found</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Document Name</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Serial No.</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence>
                      {documentsList.map((doc) => (
                        <motion.tr
                          key={doc._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          component={TableRow}
                          hover
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <DocumentIcon color="primary" />
                              {doc.documentSerialNo || 'Untitled Document'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {doc.formattedDate || new Date(doc.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Alert
                              severity={doc.status === "SIGNED" ? "success" : "warning"}
                              sx={{ display: "inline-flex", py: 0 }}
                            >
                              {doc.status || 'Pending'}
                            </Alert>
                          </TableCell>
                          <TableCell>{doc.documentSerialNo}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <Tooltip title="Preview Document">
                                <IconButton
                                  color="primary"
                                  onClick={() => handlePreview(doc.documentUrl)}
                                >
                                  <PreviewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Document">
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    setSelectedDoc(doc);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Preview Dialog */}
      <Dialog
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Document Preview
          <IconButton
            sx={{ position: 'absolute', right: 8, top: 8 }}
            onClick={() => setPreviewUrl(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
            title="Document Preview"
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this document?
          {selectedDoc && <Typography color="text.secondary">
            {selectedDoc.documentSerialNo}
          </Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleDelete(selectedDoc?._id)} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default EsignHistory;
